/**
 * This is a Web Component to handle hybrid dropdown representations (custom and native).
 * Resources:
 *  + https://www.smashingmagazine.com/2016/12/styling-web-components-using-a-shared-style-sheet/
 *  + https://css-tricks.com/striking-a-balance-between-native-and-custom-select-elements/
 *  + https://codepen.io/sandrina-p/pen/YzyOYRr?editors=1100
 */
import {html, LitElement} from 'lit'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import {customElement, property, state, query} from 'lit/decorators.js'
import { RaygunElement } from '@src/js/common/rg-element.js'

let ComponentId = 0;

@customElement('input-select')
export class SelectFormField extends RaygunElement {
  @property({ type: String, reflect: true })
  textoverflow = 'ellipsis' // 'line-break' | 'ellipsis'

  @property({ type: String })
  label = ''

  @property({ type: String})
  get value() {
    return this.selectNative ? this.selectNative.value: ''
  }

  set value(value) {
    if (this.selectNative) {
      const oldValue = this.selectNative.value
      this.selectNative.value = value
      if (oldValue !== value) {
        this.requestUpdate('value', oldValue)
      }
    }
  }

  @property({ type: Object })
  get selectedOption() {
    return this.nativeOptions.find(o => o.value === this.value)
  }

  @state({ type: String })
  triggerText = ''

  @state({ type: Boolean })
  isActive = false

  @state({ type: Number })
  selectedIndex = -1

  @state({ type: Number })
  hoverIndex = -1

  @query('.selectNative')
  selectNative

  @query('.selectCustom')
  selectCustom

  get uid() {
    const name = this.nodeName.toLowerCase()
    return `${name}-${ComponentId}`
  }

  get triggerLabel() {
    const op = this.querySelector('option')
    return op && op.disabled ? op.innerText : null
  }

  get nativeOptions() {
    return Array.from(this.querySelectorAll('option'))
  }

  get customOptions() {
    return Array.from(this.querySelectorAll('.selectCustom-option'))
  }

  createRenderRoot() {
    ComponentId++;
    return this;
  }

  async firstUpdated() {
    this.nativeOptions.forEach(op => {
      if (op.selected) {
        this.selectedIndex = Number(op.dataset.index)
      }
    })
  }

  updated() {
    this.updateSelectedCustom()
    this.updateSelectedNative()
  }

  getOptionTriggerTitle(op) {
    return op ? op.dataset.triggerTitle || op.innerText : ''
  }

  updateSelectedCustom() {
    const { selectedIndex, hoverIndex, isActive, triggerLabel } = this
    this.selectCustom.classList.toggle('isActive', isActive)
    this.selectCustom.setAttribute('aria-hidden', !isActive)
    this.customOptions.forEach((op) => {
      const index = Number(op.dataset.index)
      op.classList.toggle('isActive', selectedIndex === index)
      op.classList.toggle('isHover', hoverIndex === index)
      if (selectedIndex === index) {
        this.triggerText = this.getOptionTriggerTitle(op)
      }
    })
    if (selectedIndex === -1 || this.triggerText === '') {
      const { customOptions } = this
      const op = customOptions.length > 0 ? customOptions[0] : ''
      const defaultText = this.getOptionTriggerTitle(op)
      this.triggerText = triggerLabel || defaultText
    }
  }

  updateSelectedNative() {
    const { selectedIndex, selectNative, nativeOptions } = this

    const index = nativeOptions.findIndex(op => Number(op.dataset.index) === selectedIndex)
    console.log('updateSelectedNative', index)
    selectNative.selectedIndex = Math.max(0, index)
  }

  onSelectCustomClick() {
    this.isActive = !this.isActive
  }

  onDocumentClick = (event) => {
    if (!this.selectCustom.contains(event.target)) {
      this.isActive = false;
    }
  }

  clamp(val, min, max) {
    return Math.min(Math.max(min, val), max)
  }

  onDocumentKeyDown = (event) => {
    const { customOptions } = this
    if (!this.isActive) return
    const firstOp = customOptions[0]
    const lastOp = customOptions[customOptions.length - 1]
    const maxIndex = Number(lastOp.dataset.index)
    const minIndex = Number(firstOp.dataset.index)
    const code = event.keyCode
    const isEsc = code === 27
    const isArrowDown = code === 40
    const isArrowUp = code === 38
    const isSelect = code === 13 || code === 32
    let index = this.hoverIndex
    if (isArrowDown) index = this.clamp(index + 1, minIndex, maxIndex)
    if (isArrowUp) index = this.clamp(index - 1, minIndex, maxIndex)
    if (isEsc) {
      index = -1
      this.isActive = false
    }
    this.hoverIndex = index
    if (isSelect) {
      this.selectedIndex = index
      this.isActive = false
      this.dispatchChangeEvent()
    }
  }

  onCustomMouseEnter = (event) => {
    this.customOptions.forEach((op) => {
      if (event.target === op) {
        this.hoverIndex = Number(op.dataset.index)
      }
    })
  }

  onCustomClick = (event) => {
    this.selectedIndex = Number(event.target.dataset.index)
    this.isActive = false
    this.dispatchChangeEvent()
  }

  onNativeSelectChange = () => {
    const option = this.nativeOptions[this.selectNative.selectedIndex]
    this.selectedIndex = Number(option.dataset.index)
    this.dispatchChangeEvent()
  }

  dispatchChangeEvent() {
    setTimeout(() => {
      this.dispatchEvent(new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
      }))
    }, 0)
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick)
    document.addEventListener('keydown', this.onDocumentKeyDown)
    super.connectedCallback()
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick)
    document.removeEventListener('keydown', this.onDocumentKeyDown)
    super.disconnectedCallback()
  }

  renderNative() {
    const { uid } = this
    const select = this.querySlot('select').pop()
    select.classList.add('selectNative')
    select.setAttribute('aria-labelledby', uid)
    select.setAttribute('id', uid)
    select.onchange = this.onNativeSelectChange
    return html`
      ${select}
    `
  }

  renderCustom() {
    const { triggerLabel, triggerText, nativeOptions } = this
    let options = Array.from(nativeOptions)
    if (triggerLabel) {
      options.shift()
    }
    return html`
      <div class="selectCustom" aria-hidden="true">
        <div
          class="selectCustom-trigger"
          @click="${this.onSelectCustomClick}"
        >
          <div class="selectCustom-trigger__text">
            ${unsafeHTML(triggerText)}
          </div>
        </div>
        <div class="selectCustom-options">
          ${options.map(o => html`
            <div
              class="selectCustom-option"
              data-index="${o.dataset.index}"
              data-trigger-title="${this.getOptionTriggerTitle(o)}"
              data-value="${o.getAttribute('value')}"
              @mouseenter="${this.onCustomMouseEnter}"
              @click="${this.onCustomClick}"
            >
              ${unsafeHTML(o.innerHTML)}
            </div>
          `)}
        </div>
      </div>
    `;
  }

  renderLabel() {
    const { label, uid } = this
    if (!label) return ''
    return html`
      <span class="selectLabel" id="${uid}">
        ${unsafeHTML(label)}
      </span>
    `
  }

  render() {
    return html`
      ${this.renderLabel()}
      <div class="selectWrapper">
        ${this.renderNative()}
        ${this.renderCustom()}
      </div>
    `;
  }
}